import {
  ApolloClient,
  DocumentNode,
  NormalizedCacheObject,
} from "@apollo/client";

export enum SyncProgressStatus {
  PENDING = "pending",
  FETCHING = "fetching",
  SYNCING = "syncing",
  ERROR = "error",
  COMPLETE = "complete",
}

export type SyncProgressEvent = {
  fetched: number;
  synced: number;
  estimatedTotalItems?: number;
  status: SyncProgressStatus;
  totalItems: number;
};

export type PageQueryOptions = {
  additionalVariables?: any;
  after?: Date;
  progressCallback?: (
    event: Partial<SyncProgressEvent>
  ) => Promise<void> | void;
};

export const pageQuery = async (
  client: ApolloClient<NormalizedCacheObject>,
  key: string,
  query: DocumentNode,
  options?: PageQueryOptions
) => {
  const limit = 1000;
  let page = 1;
  let total = limit + 1;

  const additionalVariables = options?.additionalVariables;
  const progressCallback = options?.progressCallback;

  await progressCallback?.({
    status: SyncProgressStatus.FETCHING,
  });

  const r = [];
  while (page <= Math.ceil(total / limit)) {
    const { data } = await client.query({
      query,
      variables: {
        limit,
        page,
        after: options?.after,
        ...(additionalVariables || {}),
      },
    });

    if (data[key].items.length > 0) {
      r.push(...data[key].items);
    }

    total = data[key]?.meta?.totalItems ?? undefined;

    const normalizedEstimate = total === undefined ? limit * page : total;

    await progressCallback?.({
      fetched: r.length,
      estimatedTotalItems: normalizedEstimate,
      status: SyncProgressStatus.FETCHING,
      totalItems: total,
    });

    page++;

    const maybeCount = limit * page;
    progressCallback?.({
      fetched: total < maybeCount ? total : maybeCount,
      synced: 0,
      status: SyncProgressStatus.FETCHING,
      totalItems: total,
    });
  }
  return r;
};
